import { logger } from './logger'

const COOKIE_ACCESS = 'storytailor_auth_at'
const COOKIE_REFRESH = 'storytailor_auth_rt'
/** 로그아웃 후 다른 서브도메인에서 JWT 쿠키를 다시 localStorage로 붙이지 않도록 하는 표시 */
const COOKIE_LOGOUT_MARKER = 'storytailor_explicit_logout'
const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 14
const LOGOUT_MARKER_MAX_AGE_SEC = 60 * 60 * 24 * 180

export function getAuthCookieDomain(): string | null {
  if (typeof window === 'undefined') return null
  const h = window.location.hostname
  if (h === 'storytailor.co.kr' || h.endsWith('.storytailor.co.kr')) return '.storytailor.co.kr'
  return null
}

/** 운영(.storytailor.co.kr) 또는 로컬 개발(localhost)에서 쿠키 미러가 켜져 있는지 */
export function isCrossDomainAuthMirrorActive(): boolean {
  return !!getAuthCookieDomain() || isLocalhostCookieMirrorEnabled()
}

/** localhost:3000 ↔ :3001 등 포트만 다른 로컬 앱 간 JWT 공유 (운영 빌드에서는 사용 안 함) */
function isLocalhostCookieMirrorEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (process.env.NODE_ENV === 'production') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1'
}

function cookieSecureSameSite(): string {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
  return `${secure ? '; Secure' : ''}; SameSite=Lax`
}

function readCookieRaw(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  const parts = document.cookie.split('; ')
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length))
      } catch {
        return part.slice(prefix.length)
      }
    }
  }
  return null
}

function deleteCookie(name: string, domain: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; Path=/; Domain=${domain}; Max-Age=0${cookieSecureSameSite()}`
}

function setCookie(name: string, value: string, domain: string): void {
  if (typeof document === 'undefined') return
  if (value.length > 3800) {
    logger.warn('[crossDomainAuth] token too large for cookie mirror, skipping')
    return
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Domain=${domain}; Max-Age=${COOKIE_MAX_AGE_SEC}${cookieSecureSameSite()}`
}

function setShortCookie(name: string, value: string, domain: string, maxAgeSec: number): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Domain=${domain}; Max-Age=${maxAgeSec}${cookieSecureSameSite()}`
}

/** Domain 미지정 — localhost 포트 간 공유용 */
function setCookieHostOnly(name: string, value: string, maxAgeSec: number): void {
  if (typeof document === 'undefined') return
  if (value.length > 3800) {
    logger.warn('[crossDomainAuth] token too large for localhost cookie mirror, skipping')
    return
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSec}${cookieSecureSameSite()}`
}

function deleteCookieHostOnly(name: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; Path=/; Max-Age=0${cookieSecureSameSite()}`
}

/** 사용자가 로그아웃했음을 모든 *.storytailor.co.kr 에 알림 → hydrate 시 JWT 복원 안 함 */
export function markExplicitLogoutAcrossDomains(): void {
  const domain = getAuthCookieDomain()
  if (domain) {
    setShortCookie(COOKIE_LOGOUT_MARKER, '1', domain, LOGOUT_MARKER_MAX_AGE_SEC)
  }
  if (isLocalhostCookieMirrorEnabled()) {
    setCookieHostOnly(COOKIE_LOGOUT_MARKER, '1', LOGOUT_MARKER_MAX_AGE_SEC)
  }
}

export function clearExplicitLogoutMarker(): void {
  const domain = getAuthCookieDomain()
  if (domain) {
    deleteCookie(COOKIE_LOGOUT_MARKER, domain)
  }
  if (isLocalhostCookieMirrorEnabled()) {
    deleteCookieHostOnly(COOKIE_LOGOUT_MARKER)
  }
}

/** 다른 서브도메인에서 로그아웃한 뒤 이 출처 localStorage에 남은 토큰을 무시한다. */
export function isExplicitLogoutBlocked(): boolean {
  return readCookieRaw(COOKIE_LOGOUT_MARKER) === '1'
}

/**
 * 로그아웃 마커가 있으면 이 탭·출처의 localStorage 세션을 지우고 true를 반환한다.
 * (app ↔ 랜딩은 localStorage가 분리되어 있어 쿠키만으로는 상대 쪽 저장소가 비워지지 않음)
 */
export function rejectAuthBootstrapIfLoggedOut(): boolean {
  if (!isExplicitLogoutBlocked()) return false
  clearLocalAuthSessionStores()
  return true
}

/** 401·탈퇴·토큰 만료 등 강제 세션 종료 시 storage·쿠키를 explicit logout과 동일하게 정리 */
export function performForcedClientLogout(): void {
  clearLocalAuthSessionStores()
  markExplicitLogoutAcrossDomains()
  clearSharedAuthCookies()
}

/** 로그아웃 시 현재 출처의 세션 관련 저장소 정리 */
export function clearLocalAuthSessionStores(): void {
  const keys = [
    'access_token',
    'refresh_token',
    'firebase_id_token',
    'firebase_user',
    'user_account',
    'session_id',
  ]
  keys.forEach((k) => {
    try {
      localStorage.removeItem(k)
      sessionStorage.removeItem(k)
    } catch {
      /* ignore private mode 등 */
    }
  })
}

/** 로그인·갱신 후 access/refresh를 공유 쿠키에 반영(운영: .storytailor.co.kr, 로컬: host-only) */
export function syncAuthCookiesFromStorage(): void {
  const at = localStorage.getItem('access_token')
  const rt = localStorage.getItem('refresh_token')
  // 재로그인 시 이전 explicit-logout 마커를 반드시 제거 (마커가 남으면 hydrate가 차단됨)
  if (at) clearExplicitLogoutMarker()

  const domain = getAuthCookieDomain()
  if (domain) {
    if (at) setCookie(COOKIE_ACCESS, at, domain)
    else deleteCookie(COOKIE_ACCESS, domain)
    if (rt) setCookie(COOKIE_REFRESH, rt, domain)
    else deleteCookie(COOKIE_REFRESH, domain)
  }

  if (isLocalhostCookieMirrorEnabled()) {
    if (at) setCookieHostOnly(COOKIE_ACCESS, at, COOKIE_MAX_AGE_SEC)
    else deleteCookieHostOnly(COOKIE_ACCESS)
    if (rt) setCookieHostOnly(COOKIE_REFRESH, rt, COOKIE_MAX_AGE_SEC)
    else deleteCookieHostOnly(COOKIE_REFRESH)
  }
}

export function clearSharedAuthCookies(): void {
  const domain = getAuthCookieDomain()
  if (domain) {
    deleteCookie(COOKIE_ACCESS, domain)
    deleteCookie(COOKIE_REFRESH, domain)
  }
  if (isLocalhostCookieMirrorEnabled()) {
    deleteCookieHostOnly(COOKIE_ACCESS)
    deleteCookieHostOnly(COOKIE_REFRESH)
  }
}

/**
 * `/login?reauth=1`으로 들어올 때 하이드레이트 전에 호출한다.
 * 그렇지 않으면 공유 JWT 쿠키가 다시 localStorage로 붙어 자동 로그인처럼 보일 수 있다.
 */
export function applyReauthLoginBeforeHydrate(): void {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('reauth') !== '1') return
    clearLocalAuthSessionStores()
    clearSharedAuthCookies()
  } catch {
    /* ignore */
  }
}

/** 다른 출처(서브도메인 또는 로컬 다른 포트)에서 미러된 쿠키가 있으면 localStorage로 옮긴다. */
export function hydrateAuthFromSharedCookies(): void {
  const at = readCookieRaw(COOKIE_ACCESS)
  const rt = readCookieRaw(COOKIE_REFRESH)
  const logoutBlocked = readCookieRaw(COOKIE_LOGOUT_MARKER) === '1'

  if (!isCrossDomainAuthMirrorActive()) return

  if (!at && !rt && logoutBlocked) return

  if (at || rt) {
    clearExplicitLogoutMarker()
    if (at) localStorage.setItem('access_token', at)
    else localStorage.removeItem('access_token')
    if (rt) localStorage.setItem('refresh_token', rt)
    else localStorage.removeItem('refresh_token')
    return
  }

  // 공유 쿠키 미러 환경: 쿠키가 없으면 이 출처 localStorage 토큰은 stale 로 간주
  clearLocalAuthSessionStores()
}
